import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

class TrackedDirectUpload {
  constructor(file, url, onProgress) {
    this.file = file
    this.onProgress = onProgress
    this.upload = new DirectUpload(file, url, this)
  }

  create(callback) {
    this.upload.create(callback)
  }

  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", (event) => {
      if (!event.lengthComputable) return

      this.onProgress(Math.round((event.loaded / event.total) * 100))
    })
  }
}

export default class extends Controller {
  static targets = ["input", "list", "status", "submit"]
  static values = {
    uploadUrl: String,
    acceptedExtensions: String,
    maxFiles: Number,
    maxFileSize: Number,
    maxTotalSize: Number,
    composerDisabled: Boolean
  }

  connect() {
    this.files = new Map()
    this.pendingUploads = 0
  }

  choose() {
    if (this.composerDisabledValue) return

    this.inputTarget.click()
  }

  select(event) {
    this.addFiles(event.currentTarget.files)
    event.currentTarget.value = ""
  }

  dragEnter(event) {
    if (!this.hasFiles(event) || this.composerDisabledValue) return

    event.preventDefault()
    this.composerElement.classList.add("is-dragging")
  }

  dragOver(event) {
    if (!this.hasFiles(event) || this.composerDisabledValue) return

    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
  }

  dragLeave(event) {
    if (event.relatedTarget && this.element.contains(event.relatedTarget)) return

    this.composerElement.classList.remove("is-dragging")
  }

  drop(event) {
    if (!this.hasFiles(event) || this.composerDisabledValue) return

    event.preventDefault()
    this.composerElement.classList.remove("is-dragging")
    this.addFiles(event.dataTransfer.files)
  }

  remove(event) {
    const uploadId = event.currentTarget.dataset.uploadId
    const entry = this.files.get(uploadId)
    if (!entry || entry.pending) return

    entry.hiddenInput?.remove()
    entry.element.remove()
    this.files.delete(uploadId)
    this.refreshList()
  }

  addFiles(fileList) {
    Array.from(fileList).forEach((file) => {
      const error = this.validationError(file)
      if (error) {
        this.showError(error)
      } else {
        this.upload(file)
      }
    })
  }

  validationError(file) {
    if (this.files.size >= this.maxFilesValue) {
      return `Attach no more than ${this.maxFilesValue} files per message.`
    }
    if (file.size > this.maxFileSizeValue) {
      return `${file.name} is larger than ${this.megabytes(this.maxFileSizeValue)} MB.`
    }

    const extension = `.${file.name.split(".").pop()?.toLowerCase()}`
    if (!this.acceptedExtensionSet.has(extension)) {
      return `${file.name} is not a supported file type.`
    }

    if (this.totalSize + file.size > this.maxTotalSizeValue) {
      return `Attachments must be ${this.megabytes(this.maxTotalSizeValue)} MB or less in total.`
    }

    return null
  }

  upload(file) {
    const uploadId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
    const element = this.buildFileElement(file, uploadId)
    const entry = { file, element, pending: true, hiddenInput: null }
    this.files.set(uploadId, entry)
    this.listTarget.appendChild(element)
    this.pendingUploads += 1
    this.refreshList()

    const upload = new TrackedDirectUpload(file, this.uploadUrlValue, (progress) => {
      this.updateFileStatus(element, `${progress}%`)
    })

    upload.create((error, blob) => {
      this.pendingUploads -= 1
      if (error) {
        element.remove()
        this.files.delete(uploadId)
        this.showError(`${file.name} could not be uploaded. Try again.`)
      } else {
        entry.pending = false
        entry.hiddenInput = this.buildHiddenInput(blob.signed_id)
        this.element.appendChild(entry.hiddenInput)
        this.updateFileStatus(element, this.humanFileSize(file.size))
        element.querySelector("button").disabled = false
      }
      this.refreshList()
    })
  }

  buildFileElement(file, uploadId) {
    const item = document.createElement("div")
    item.className = "assistant-composer__attachment"

    const icon = document.createElement("i")
    icon.className = file.type.startsWith("image/") ? "fa-solid fa-image" : "fa-solid fa-file-lines"
    icon.setAttribute("aria-hidden", "true")

    const name = document.createElement("span")
    name.className = "text-truncate"
    name.textContent = file.name

    const status = document.createElement("small")
    status.className = "fg-2 flex-shrink-0"
    status.dataset.attachmentStatus = ""
    status.textContent = "0%"

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "btn btn-sm btn-icon rounded-circle"
    remove.disabled = true
    remove.dataset.action = "assistant-attachments#remove"
    remove.dataset.uploadId = uploadId
    remove.setAttribute("aria-label", `Remove ${file.name}`)
    remove.innerHTML = '<i class="fa-solid fa-xmark" aria-hidden="true"></i>'

    item.append(icon, name, status, remove)
    return item
  }

  buildHiddenInput(signedId) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "assistant_message[attachments][]"
    input.value = signedId
    return input
  }

  updateFileStatus(element, value) {
    element.querySelector("[data-attachment-status]").textContent = value
  }

  refreshList() {
    this.listTarget.classList.toggle("d-none", this.files.size === 0)
    this.statusTarget.classList.toggle("d-none", this.pendingUploads === 0)
    this.statusTarget.classList.remove("fg-danger")
    this.statusTarget.textContent = this.pendingUploads > 0 ? `Uploading ${this.pendingUploads} ${this.pendingUploads === 1 ? "file" : "files"}…` : ""
    this.submitTarget.disabled = this.composerDisabledValue || this.pendingUploads > 0
    this.element.setAttribute("aria-busy", this.pendingUploads > 0 ? "true" : "false")
  }

  showError(message) {
    this.statusTarget.classList.remove("d-none")
    this.statusTarget.classList.add("fg-danger")
    this.statusTarget.textContent = message
    window.showToast?.(message, "warning")
  }

  hasFiles(event) {
    return Array.from(event.dataTransfer?.types || []).includes("Files")
  }

  get acceptedExtensionSet() {
    return new Set(this.acceptedExtensionsValue.split(","))
  }

  get totalSize() {
    return Array.from(this.files.values()).reduce((sum, entry) => sum + entry.file.size, 0)
  }

  get composerElement() {
    return this.element.closest(".assistant-composer")
  }

  megabytes(bytes) {
    return Math.round(bytes / (1024 * 1024))
  }

  humanFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
}
